/* global theme */

/*
  PSM Promotions — self-hosted Monk replacement (Phase 1: cross-sell + free gift), B2B-aware.

  Reads window.theme.promo (audience + campaigns from `promo_campaign` metaobjects).
  - Cross-sell: render matching campaigns' items into #psm-xsell; "Add" stamps line
    attributes `_promo` + `_promo_pct` (the deployed cart-transform function prices the % off).
  - Free gift: auto-add the campaign's gift (stamped `_gift: auto` → priced free by the function)
    when conditions match; auto-remove when they don't; honor customer opt-out (session).
  - Progress bar: show progress toward a free-gift cart-value milestone into #psm-progress.

  Inert unless window.theme.promo.campaigns is non-empty. Mirrors psm-tariff-surcharge.js.
*/
(function () {
  "use strict";
  if (window.__psmPromoInit) return;
  window.__psmPromoInit = true;

  var cfg = (window.theme && window.theme.promo) || null;
  if (!cfg || !Array.isArray(cfg.campaigns) || cfg.campaigns.length === 0) return;

  var aud = cfg.audience || {};
  var OPT_OUT_KEY = "psm_gift_optout";
  var reconciling = false;
  var debounceTimer = null;

  function parseJSON(s, fallback) {
    try { return s ? JSON.parse(s) : fallback; } catch (e) { return fallback; }
  }

  function scheduleActive(c) {
    var n = new Date();
    if (c.start && new Date(c.start) > n) return false;
    if (c.end && new Date(c.end) < n) return false;
    return true;
  }

  function audienceMatches(c) {
    var list = Array.isArray(c.audience) ? c.audience : (c.audience ? [c.audience] : []);
    if (list.length === 0) return true; // no audience = everyone
    var tags = String(aud.tags || "").toLowerCase().split(",").map(function (t) { return t.trim(); });
    return list.some(function (a) {
      if (a === "retail") return !aud.isB2B;
      if (a === "any_b2b") return !!aud.isB2B;
      if (a.indexOf("company_location:") === 0) return String(aud.locationId || "") === a.slice(17);
      if (a.indexOf("catalog:") === 0) return String(aud.catalogId || "") === a.slice(8);
      if (a.indexOf("tag:") === 0) return tags.indexOf(a.slice(4).toLowerCase()) !== -1;
      return false;
    });
  }

  function cartHasProduct(cart, ids) {
    return cart.items.some(function (i) {
      return ids.indexOf(String(i.id)) !== -1 || ids.indexOf(String(i.variant_id)) !== -1 || ids.indexOf(String(i.product_id)) !== -1;
    });
  }
  function cartHasVendor(cart, vendors) {
    var vs = vendors.map(function (v) { return String(v).toLowerCase(); });
    return cart.items.some(function (i) { return vs.indexOf(String(i.vendor || "").toLowerCase()) !== -1; });
  }
  function cartHasProductType(cart, types) {
    var ts = types.map(function (t) { return String(t).toLowerCase(); });
    return cart.items.some(function (i) { return ts.indexOf(String(i.product_type || "").toLowerCase()) !== -1; });
  }
  // Tags aren't in /cart.js — fetch per cart product (cached) only when a tag campaign exists.
  var productTagsCache = {};
  function needsTags() {
    return cfg.campaigns.some(function (c) { return (c.conditions || []).some(function (x) { return x.field === "tag"; }); });
  }
  function ensureProductMeta(cart) {
    if (!needsTags()) return Promise.resolve();
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    var uniq = {};
    cart.items.forEach(function (i) { if (i.handle && productTagsCache[i.product_id] === undefined) uniq[i.handle] = i.product_id; });
    var handles = Object.keys(uniq);
    if (!handles.length) return Promise.resolve();
    return Promise.all(handles.map(function (h) {
      return fetch(root + "products/" + h + ".js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p) productTagsCache[p.id] = p.tags || []; })
        .catch(function () { /* tags best-effort */ });
    }));
  }
  function cartHasTag(cart, tags) {
    var want = tags.map(function (t) { return String(t).toLowerCase(); });
    return cart.items.some(function (i) {
      return (productTagsCache[i.product_id] || []).some(function (x) { return want.indexOf(String(x).toLowerCase()) !== -1; });
    });
  }
  function conditionsMatch(c, cart) {
    var conds = c.conditions || [];
    if (!conds.length) return true;
    return conds.every(function (cond) {
      var vals = cond.values || [];
      if (cond.field === "product") return cartHasProduct(cart, vals.map(String));
      if (cond.field === "vendor") return cartHasVendor(cart, vals);
      if (cond.field === "product_type") return cartHasProductType(cart, vals);
      if (cond.field === "tag") return cartHasTag(cart, vals);
      if (cond.field === "cart_value") return (cart.total_price || 0) >= parseFloat(vals[0]) * 100;
      return false; // collection: not resolvable client-side (documented limitation)
    });
  }

  function matching(type, placement, cart) {
    return cfg.campaigns
      .filter(function (c) { return c.type === type && (c.placement || []).indexOf(placement) !== -1; })
      .filter(scheduleActive)
      .filter(audienceMatches)
      .filter(function (c) { return conditionsMatch(c, cart); })
      .sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
  }

  function getCart() {
    return fetch(theme.routes.cart + ".js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); });
  }
  function findLineByVariant(cart, vid) {
    for (var i = 0; i < cart.items.length; i += 1) if (cart.items[i].variant_id === vid) return cart.items[i];
    return null;
  }
  function addLine(variantId, props) {
    return fetch(theme.routes.cartAdd, {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: props }] }),
    });
  }
  function removeLine(key) {
    return fetch(theme.routes.cartChange, {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
  }

  function optedOut() { try { return JSON.parse(sessionStorage.getItem(OPT_OUT_KEY) || "[]"); } catch (e) { return []; } }
  function setOptedOut(a) { try { sessionStorage.setItem(OPT_OUT_KEY, JSON.stringify(a)); } catch (e) { /* ignore */ } }

  // --- Free gift reconcile ---
  function reconcileGifts(cart) {
    var want = {};
    matching("free_gift", "cart", cart).forEach(function (c) {
      var vid = Number((c.gift || {}).variantId);
      if (vid) want[vid] = c;
    });
    var outs = optedOut();
    var ops = [];
    Object.keys(want).forEach(function (vidStr) {
      var vid = Number(vidStr), c = want[vid];
      if (outs.indexOf(c.id) !== -1) return;             // opted out
      if (!findLineByVariant(cart, vid)) ops.push(addLine(vid, { _gift: "auto", _gift_campaign: c.id }));
    });
    cart.items.forEach(function (item) {
      if (item.properties && item.properties._gift === "auto" && !want[item.variant_id]) ops.push(removeLine(item.key));
    });
    return ops.length ? Promise.all(ops).then(function () { return true; }) : Promise.resolve(false);
  }

  // --- display helpers ---
  function money(cents) { return "$" + (Number(cents || 0) / 100).toFixed(2); }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch];
    });
  }

  // Collect not-in-cart cross-sell items for a placement ("cart" | "checkout").
  function offerItems(cart, placement) {
    var rows = [], seen = {};
    matching("cross_sell", placement, cart).forEach(function (c) {
      (c.items || []).forEach(function (it) {
        var vid = String(it.variant_id);
        if (seen[vid]) return;
        seen[vid] = 1;
        if (cart.items.some(function (i) { return String(i.variant_id) === vid; })) return; // already in cart
        rows.push({ c: c, it: it });
      });
    });
    return rows;
  }

  // --- Cross-sell render (cart page + drawer both carry .psm-xsell) ---
  function cardHtml(rows) {
    return '<div class="psm-xsell__title">People also bought</div><div class="psm-xsell__grid">' + rows.map(function (x) {
      var it = x.it, pct = it.percent || 0;
      var now = pct ? Math.round(it.price * (1 - pct / 100)) : it.price;
      var price = pct
        ? '<span class="psm-xsell__was">' + money(it.price) + '</span> <span class="psm-xsell__now">' + money(now) + '</span> <span class="psm-xsell__off">' + pct + '% off</span>'
        : '<span class="psm-xsell__now">' + money(it.price) + '</span>';
      return '<div class="psm-xsell__card">' +
        (it.image ? '<img class="psm-xsell__img" src="' + esc(it.image) + '" alt="" loading="lazy">' : '<div class="psm-xsell__img psm-xsell__img--ph"></div>') +
        '<div class="psm-xsell__name">' + esc(it.title) + '</div>' +
        '<div class="psm-xsell__price">' + price + '</div>' +
        '<button type="button" class="psm-xsell__add btn btn--secondary tap-target" data-variant="' + it.variant_id + '" data-campaign="' + esc(x.c.id) + '" data-pct="' + pct + '" data-was="' + it.price + '">Add</button>' +
        '</div>';
    }).join("") + '</div>';
  }
  function bindAdd(host) {
    host.querySelectorAll(".psm-xsell__add").forEach(function (btn) {
      if (btn.__bound) return;
      btn.__bound = 1;
      btn.addEventListener("click", function () {
        btn.disabled = true;
        addLine(Number(btn.dataset.variant), { _promo: btn.dataset.campaign, _promo_pct: btn.dataset.pct, _promo_was: btn.dataset.was })
          .then(function () { document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh")); });
      });
    });
  }
  function renderCrossSell(cart) {
    var hosts = document.querySelectorAll(".psm-xsell");
    if (!hosts.length) return;
    var rows = offerItems(cart, "cart");
    // Avoid overlap: hide every native "You may also like" (cart page AND drawer) while our offer shows.
    Array.prototype.forEach.call(document.querySelectorAll(".cart-page-recommendations"), function (el) {
      el.style.display = rows.length ? "none" : "";
    });
    var html = rows.length ? cardHtml(rows) : "";
    Array.prototype.forEach.call(hosts, function (host) { host.innerHTML = html; if (html) bindAdd(host); });
  }

  // --- Checkout offer relay ---
  // The theme already matches campaigns reliably, so it computes the CHECKOUT
  // cross-sell here and hands it to the checkout UI extension via a cart
  // attribute. The extension just renders it — no metaobject query, no
  // trigger/audience/B2B re-evaluation in the checkout runtime.
  function writeCheckoutOffer(cart) {
    var off = offerItems(cart, "checkout").map(function (x) {
      return { v: x.it.variant_id, p: x.it.percent || 0, t: x.it.title || "", pr: x.it.price || 0, c: x.c.id };
    });
    var val = off.length ? JSON.stringify(off) : "";
    var cur = (cart.attributes && cart.attributes.__psm_xsell) || "";
    if (val === cur) return Promise.resolve();
    return fetch(theme.routes.cartUpdate, {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { __psm_xsell: val } }),
    }).catch(function () { /* best-effort */ });
  }

  // --- Progress bar (free-gift cart-value milestone) ---
  function renderProgress(cart) {
    var hosts = document.querySelectorAll(".psm-progress");
    if (!hosts.length) return;
    var camps = cfg.campaigns
      .filter(function (c) { return c.type === "free_gift" && c.progressBar && (c.placement || []).indexOf("cart") !== -1; })
      .filter(scheduleActive).filter(audienceMatches);
    var cv = camps.length ? (camps[0].conditions || []).filter(function (x) { return x.field === "cart_value"; })[0] : null;
    var html = "";
    if (cv) {
      var threshold = parseFloat((cv.values || [])[0]) * 100;
      var pct = threshold > 0 ? Math.min(100, Math.round((cart.total_price / threshold) * 100)) : 0;
      var remaining = Math.max(0, threshold - cart.total_price);
      var text = String(camps[0].progressText || "").replace("{remaining}", "$" + (remaining / 100).toFixed(2));
      html = '<div class="psm-progress__text">' + (remaining > 0 ? text : "Free gift unlocked!") +
        '</div><div class="psm-progress__bar"><span style="width:' + pct + '%"></span></div>';
    }
    Array.prototype.forEach.call(hosts, function (host) { host.innerHTML = html; });
  }

  function reconcile() {
    if (reconciling) return Promise.resolve();
    reconciling = true;
    return getCart()
      .then(function (cart) {
        return ensureProductMeta(cart).then(function () {
          renderCrossSell(cart);
          renderProgress(cart);
          writeCheckoutOffer(cart);
          return reconcileGifts(cart).then(function (changed) {
            if (changed) document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
          });
        });
      })
      .catch(function (e) { if (window.console) console.warn("[psm-promo] reconcile failed", e); }) // eslint-disable-line no-console
      .finally(function () { reconciling = false; });
  }

  function schedule() {
    if (reconciling) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 250);
  }

  document.addEventListener("dispatch:cart-drawer:refresh", schedule);
  document.addEventListener("on:line-item:change", schedule);
  document.addEventListener("on:cart:add", schedule);

  // Remember opt-out when a customer removes an auto-added gift line.
  document.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest(".js-remove-item");
    if (!btn) return;
    var row = btn.closest(".cart-item");
    if (row && row.getAttribute("data-gift") === "true") {
      var cid = row.getAttribute("data-gift-campaign");
      if (cid) { var outs = optedOut(); if (outs.indexOf(cid) === -1) { outs.push(cid); setOptedOut(outs); } }
    }
  }, true);

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", schedule);
  else schedule();
})();
